import { useEffect, useRef, useState } from "react"

const RETRY_MS = 150
const MAX_RETRIES = 8

export interface TourTarget {
	rect: DOMRect
	radius: string
}

function measure(element: HTMLElement): TourTarget {
	return {
		rect: element.getBoundingClientRect(),
		radius: window.getComputedStyle(element).borderRadius,
	}
}

export function useTourTarget(selector: string | null, onMissing: () => void): TourTarget | null {
	const [target, setTarget] = useState<TourTarget | null>(null)
	const onMissingRef = useRef(onMissing)
	onMissingRef.current = onMissing

	useEffect(() => {
		setTarget(null)
		if (!selector) return

		let cancelled = false
		let attempt = 0
		let observer: ResizeObserver | null = null
		let retryTimer: number | null = null

		const stopTracking = () => {
			observer?.disconnect()
			observer = null
			window.removeEventListener("scroll", update, true)
			window.removeEventListener("resize", update)
		}

		function update() {
			const el = document.querySelector<HTMLElement>(selector as string)
			if (el) setTarget(measure(el))
		}

		const tryFind = () => {
			if (cancelled) return
			const el = document.querySelector<HTMLElement>(selector as string)
			if (!el) {
				attempt += 1
				if (attempt > MAX_RETRIES) {
					onMissingRef.current()
					return
				}
				retryTimer = window.setTimeout(tryFind, RETRY_MS)
				return
			}

			el.scrollIntoView({ block: "center", behavior: "smooth" })
			setTarget(measure(el))
			observer = new ResizeObserver(update)
			observer.observe(el)
			window.addEventListener("scroll", update, true)
			window.addEventListener("resize", update)
		}

		tryFind()

		return () => {
			cancelled = true
			if (retryTimer !== null) window.clearTimeout(retryTimer)
			stopTracking()
		}
	}, [selector])

	return target
}
